import { useFeatureAccess } from "@src/auth/permissions/FeatureAccessContext";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { VITE_UPLOAD_BASE_URL } from "@src/env";
import { attachmentCipher } from "@src/global_functions/attachments/attachmentUtils";
import FetchClients, {
  ClientOutput,
} from "@src/global_functions/backpackSdk/client";
import {
  bugsnagGeneralErrorHandler,
  bugsnagPostgrestErrorHandler,
} from "@src/global_functions/postgrestApi/common";
import request from "@src/global_functions/request";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { throwBackpackResponseError } from "../errors";

const attachments = {
  getProjectAttachmentPreviews,
  uploadProjectAttachment,
  deleteProjectAttachment,
  downloadProjectAttachment,
  getBackpackProjectAttachmentInformation,
  useQueryBackpackProjectAttachmentInformation,
  useDeleteProjectAttachment,
  usePatchProjectAttachment,
};

const baseUrl = `${VITE_UPLOAD_BASE_URL}/project-attachment`;

const PROJECT_ATTACHMENT_KEY = "project-attachments";

// endpoint requires {feature: "Projects", access: "read"}
// header is optional so this stays call-compatible with the shared serviceMap
// union in attachmentsQueries.ts; the projects consumer always supplies it.
async function getProjectAttachmentPreviews(
  projectId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader = {}
) {
  const { data, error } = await FetchClients.d3.GET(
    "/project-attachment/preview",
    {
      params: {
        query: {
          project_id: projectId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }
  if (data) return data;
}

// endpoint requires {feature: "Projects", access: "edit"}
// header is optional so this stays call-compatible with the shared serviceMap
// union in attachmentsQueries.ts; the projects consumer always supplies it.
async function uploadProjectAttachment(
  projectId: number,
  file: File,
  checkFeatureAccessHeader: CheckFeatureAccessHeader = {}
): Promise<void> {
  return request
    .post(`${baseUrl}/upload?project_id=${projectId}&filename=${file.name}`)
    .set("Content-Type", "text/plain")
    .set(checkFeatureAccessHeader)
    .send(file)
    .then((res) => res.body)
    .catch((error) => bugsnagGeneralErrorHandler(error));
}

// endpoint requires {feature: "Projects", access: "edit"}
async function deleteProjectAttachment(
  fileIds: (number | string)[] | number | string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const ids = Array.isArray(fileIds) ? fileIds.map(Number) : [Number(fileIds)];
  const { response, error } = await FetchClients.d3.DELETE(
    "/project-attachment/delete",
    { params: { query: { file_ids: ids } }, headers: checkFeatureAccessHeader }
  );

  if (response.ok) return;
  throw new Error((error as unknown as Error)?.message || String(error));
}

function useDeleteProjectAttachment(siteId: number, projectId?: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    (
      fileIds: (number | string)[] | number | string,
      checkFeatureAccessHeader
    ) => deleteProjectAttachment(fileIds, checkFeatureAccessHeader),
    {
      onSuccess: () => {
        return invalidateProjectAttachmentQueries(
          queryClient,
          siteId,
          projectId
        );
      },
    }
  );
}
// endpoint requires {feature: "Projects", access: "read"}
async function downloadProjectAttachment(
  {
    attachmentId,
    disposition,
  }: {
    attachmentId: string | number;
    disposition?: "Attachment" | "Inline";
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<string | void> {
  const { data, error } = await FetchClients.d3.GET(
    "/project-attachment/download",
    {
      params: {
        query: {
          attachment_id: Number(attachmentId),
          disposition,
        },
      },
      parseAs: "text",
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  if (error) {
    return bugsnagGeneralErrorHandler(error);
  }
}
// endpoint requires {feature: "Projects", access: "read"}
async function getBackpackProjectAttachmentInformation(
  {
    project_id,
    site_id,
  }: {
    project_id?: number;
    site_id?: number;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.projects.GET("/attachments", {
    params: {
      query: {
        project_id,
        site_id,
      },
    },
    headers: checkFeatureAccessHeader,
  });
  if (data) {
    return attachmentCipher(data) as ProjectAttachment[];
  }
  bugsnagPostgrestErrorHandler(error);
  return [];
}

export type ProjectAttachment = ClientOutput["projects"]["ProjectAttachment"];

function useQueryBackpackProjectAttachmentInformation<
  TSelect = ProjectAttachment[]
>(
  { project_id, site_id }: { project_id?: number; site_id?: number },
  select?: (data?: ProjectAttachment[]) => TSelect
) {
  const siteKey = typeof site_id === "number" && `site-${site_id}`;
  const projectKey = typeof project_id === "number" && `project-${project_id}`;

  const featureAccess = useFeatureAccess("Projects");

  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    (checkFeatureAccessHeader) =>
      getBackpackProjectAttachmentInformation(
        { project_id, site_id },
        checkFeatureAccessHeader
      ),
    {
      queryKey: [PROJECT_ATTACHMENT_KEY, siteKey || projectKey || undefined],
      select,
      enabled: !!featureAccess,
      staleTime: 1000 * 60,
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
async function patchProjectAttachment(
  {
    attachmentId,
    filename,
  }: {
    attachmentId: number;
    filename: string;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { response, error } = await FetchClients.d3.PATCH(
    "/project-attachment/edit",
    {
      params: {
        query: {
          file_id: attachmentId,
        },
      },
      body: { filename },
      headers: checkFeatureAccessHeader,
    }
  );

  if (response.ok) return;
  throwBackpackResponseError(error);
}

function invalidateProjectAttachmentQueries(
  queryClient: QueryClient,
  siteId: number,
  projectId?: number
) {
  return Promise.allSettled([
    queryClient.invalidateQueries({
      queryKey: [PROJECT_ATTACHMENT_KEY, `site-${siteId}`],
    }),
    typeof projectId === "number" &&
      queryClient.invalidateQueries({
        queryKey: [PROJECT_ATTACHMENT_KEY, `project-${projectId}`],
      }),
  ]);
}

function usePatchProjectAttachment(siteId: number, projectId?: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    (
      {
        attachmentId,
        filename,
      }: {
        attachmentId: number;
        filename: string;
      },
      checkFeatureAccessHeader
    ) =>
      patchProjectAttachment(
        { attachmentId, filename },
        checkFeatureAccessHeader
      ),
    {
      onSuccess: () => {
        return invalidateProjectAttachmentQueries(
          queryClient,
          siteId,
          projectId
        );
      },
    }
  );
}

export default attachments;
